import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { InnerBox } from "@/components/elements/InnerBox/InnerBox";
import { Table } from "@/components/elements/Table/Table";
import { Button } from "@/components/elements/Button";
import { Modal } from "@/components/elements/Modal";
import { useTable } from "@/hooks/useTable";
import { bulkDisableCompanyUserApi } from "@/api/services/main/company-user";
import { TCompanyUser } from "@/api/entities/company-user.entity";
import { USER_ACTIVE_STATUS_LABEL } from "@/types/enum";
import { convertDateFromIsoString } from "@/features/company/utiles/datetime";
import { text } from "@/theme/colors";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { IconBlock, IconPlus } from "@/assets/icons";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { showError } from "@/utils/error";
import { useDialog } from "@/hooks/useDialog";

type CompanyUserSectionProps = {
  users: TCompanyUser[];
  isLoading: boolean;
  companyId: string | undefined;
};

export const CompanyUserSection: FC<CompanyUserSectionProps> = ({ users, isLoading, companyId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirmDisable = useDialog();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const columns: MRT_ColumnDef<TCompanyUser>[] = useMemo(
    () => [
      {
        header: "ユーザー名",
        id: "fullName",
        accessorKey: "fullName",
        size: 102,
      },
      {
        header: "メールアドレス",
        id: "email",
        accessorKey: "email",
        size: 278,
      },
      {
        header: "ステータス",
        id: "activeStatus",
        accessorKey: "activeStatus",
        size: 102,
        Cell: ({ row }) => USER_ACTIVE_STATUS_LABEL[row.original.activeStatus],
      },
      {
        header: "登録日",
        id: "createdAt",
        accessorKey: "createdAt",
        size: 106,
        Cell: ({ row }) => convertDateFromIsoString(row.original.createdAt ?? ""),
      },
      {
        header: "最終ログイン",
        id: "lastLoginAt",
        accessorKey: "lastLoginAt",
        size: 116,
        Cell: ({ row }) => (row.original.lastLoginTime ?  convertDateFromIsoString(row.original.lastLoginTime) : "-"),
      },
      {
        header: "",
        id: "actions",
        size: 80,
        Cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(getDynamicRoute(AppRoutes.companyUserEdit, { companyId, userId: row.original.id }))}
          >
            編集
          </Button>
        ),
      },
    ],
    [navigate, companyId],
  );

  const { table } = useTable({
    columns,
    rows: users,
    enableRowSelection: (row) => row.original.activeStatus === "active",
    pinningColumnsLeft: ["mrt-row-select"],
  });

  const rowSelectionKeys = useMemo(
    () => table.getSelectedRowModel().rows.map((row) => String(row.original.id)),
    [table.getState().rowSelection],
  );

  const handleBulkDisable = async () => {
    if (!companyId || rowSelectionKeys.length === 0) return;
    try {
      setIsSubmit(true);

      const agreeToDisable = await confirmDisable.show();
      if (!agreeToDisable) return;

      const userIds = rowSelectionKeys.map(Number);
      await bulkDisableCompanyUserApi(companyId, userIds);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_LIST, companyId] });
      toast.success(API_SUCCESS_MESSAGE);

      table.resetRowSelection();
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmit(false);
    }
  };
  const onCancelConfirm = () => {
    setIsSubmit(false);
    confirmDisable.cancel();
  };

  return (
    <InnerBox
      title={`発行済みユーザー (${users.length})`}
      showDivider={false}
      rightContent={
        <Button variant="outline" size="sm" startIcon={<IconPlus />} onClick={() => navigate(getDynamicRoute(AppRoutes.companyUserCreate, { companyId }))}>
          新規ユーザー
        </Button>
      }
    >
      <Typography variant="body14" color={text.secondary} component="p" mt={3} mb={2}>
        所属機関のユーザーは、学習者情報や受講状況の閲覧のみ可能です。
      </Typography>
      <Table table={table} isLoading={isLoading} />
      <Stack direction="row" justifyContent="flex-end" mt={1}>
        <Button
          variant="danger"
          size="sm"
          disabled={rowSelectionKeys.length === 0 || isSubmit}
          onClick={handleBulkDisable}
          startIcon={<IconBlock />}
        >
          選択したユーザーを一括無効化
        </Button>
      </Stack>
      <ConfirmDisableModal
        open={confirmDisable.open}
        onClose={onCancelConfirm}
        onOk={confirmDisable.confirm}
        message={`選択したユーザーを無効化してもよろしいですか？`}
      />
    </InnerBox>
  );
};


type ConfirmDisableModalProps = {
  open: boolean;
  onClose: () => void;
  onOk: () => void;
  message?: string;
};
const ConfirmDisableModal: FC<ConfirmDisableModalProps> = ({ open, onClose, onOk, message }) => (
  <Modal
    isOpen={open}
    onClose={onClose}
    labelClose="いいえ"
    labelOk="はい"
    onOk={onOk}
  >
    <Typography variant="body14" component="p" sx={{ paddingY: 2 }}>
      {message}
    </Typography>
  </Modal>
);
